// @mui
import { Button, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import { alpha, SxProps, Theme, useTheme } from '@mui/material/styles';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';

// ----------------------------------------------------------------------

const visuallyHidden = {
  border: 0,
  margin: -1,
  padding: 0,
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  position: 'absolute',
  whiteSpace: 'nowrap',
  clip: 'rect(0 0 0 0)',
} as const;

// ----------------------------------------------------------------------

export interface ITableHeadCustomType {
  colSpan?: number;
  id?: string;
  label: string;
  align?: 'left' | 'right' | 'center' | 'inherit' | 'justify';
  width?: string | number;
  minWidth?: string | number;
  maxWidth?: string | number;
  children?: ITableHeadCustomType[];
  rowSpan?: number;
  show?: boolean;
  textWrap?: 'inherit' | 'nowrap' | 'wrap' | 'wrap-reverse';
  disableSort?: boolean;
}

type Props = {
  order?: 'asc' | 'desc';
  orderBy?: string;
  headLabel: ITableHeadCustomType[];
  rowCount?: number;
  numSelected?: number;
  onSort?: (id: string) => void;
  onSelectAllRows?: (checked: boolean) => void;
  batchAction?: () => void;
  buttonText?: string;

  sx?: SxProps<Theme>;
  data?: any[];
  selected?: string[];
  setSelected?: (selected: string[]) => void;
};

export default function TableHeadCustom({
  order,
  orderBy,
  rowCount = 0,
  headLabel,
  numSelected = 0,
  onSort,
  onSelectAllRows,
  sx,
  data,
  selected,
  setSelected,
  batchAction,
  buttonText,
  showCheckbox,
}: Readonly<Props>) {
  const getOrder = order === 'desc' ? 'sorted descending' : 'sorted ascending';
  const getOrderBy = (headCell: ITableHeadCustomType) => {
    return orderBy === headCell.id ? <Box sx={{ ...visuallyHidden }}>{getOrder}</Box> : null;
  };
  const getDirection = (headCell: ITableHeadCustomType) => {
    return orderBy === headCell.id ? order : 'asc';
  };
  const theme = useTheme();
  const childHeaderBackground = alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.08);

  const visibleColumnCount = headLabel.filter((head) => head.show).reduce((acc, head) => acc + (head.colSpan || 1), 0);

  return (
    <TableHead sx={sx}>
      {numSelected > 0 ? (
        // Custom Selection Toolbar Row (like the one in the image)
        (<TableRow sx={{ height: 48 }}>
          {showCheckbox && (
            <TableCell padding="checkbox" sx={{ background: alpha(theme.palette.primary.main, 0.08), width: 48 }}>
              <Checkbox indeterminate={!!numSelected && numSelected < rowCount} checked={!!rowCount && numSelected === rowCount} onChange={onSelectAllRows} />
            </TableCell>
          )}
          {/* This cell spans all visible columns minus the checkbox column */}
          <TableCell colSpan={visibleColumnCount} sx={{ background: alpha(theme.palette.primary.main, 0.08) }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="button">{numSelected} selected</Typography>
              {buttonText && (
                <Button variant="contained" color="primary" onClick={batchAction}>
                  {buttonText}
                </Button>
              )}
            </Box>
          </TableCell>
        </TableRow>)
      ) : (
        <>
          <TableRow>
            {onSelectAllRows && showCheckbox && (
              <TableCell padding="checkbox">
                <Checkbox indeterminate={!!numSelected && numSelected < rowCount} checked={!!rowCount && numSelected === rowCount} onChange={onSelectAllRows} />
              </TableCell>
            )}

            {headLabel.map(
              (headCell) =>
                headCell.show && (
                  <TableCell
                    colSpan={headCell.colSpan || 1}
                    id={headCell.label}
                    rowSpan={headCell.rowSpan || 1}
                    key={headCell.id}
                    align={headCell.align || 'left'}
                    sortDirection={orderBy === headCell.id ? order : false}
                    sx={{
                      width: headCell.width,
                      minWidth: headCell.minWidth,
                      maxWidth: headCell.maxWidth,
                      textWrap: headCell.textWrap ?? 'inherit',
                    }}
                  >
                    {onSort && !headCell.disableSort ? (
                      <TableSortLabel hideSortIcon active={orderBy === headCell.id} direction={getDirection(headCell)} onClick={() => onSort(headCell.id)}>
                        {headCell.label}
                        {getOrderBy(headCell)}
                      </TableSortLabel>
                    ) : (
                      headCell.label
                    )}
                  </TableCell>
                )
            )}
          </TableRow>
          <TableRow>
            {headLabel.map((headCell) =>
              headCell.children
                ? headCell.children.map((child) => (
                    <TableCell
                      key={child.id}
                      align={child.align || 'left'}
                      sx={{
                        width: child.width,
                        minWidth: child.minWidth,
                        maxWidth: child.maxWidth,
                        bgcolor: childHeaderBackground,
                      }}
                    >
                      <Typography variant="subheader">{child.label}</Typography>
                    </TableCell>
                  ))
                : null
            )}
          </TableRow>
        </>
      )}
    </TableHead>
  );
}
